import Helper from "@/services/Helper";
import localforage from 'localforage';

class Cache
{
    /**
     * Stores a value in localForage after serializing it.
     * @param name - The key under which to store the value.
     * @param value - The value to store.
     * @returns A Promise that resolves when the value is stored.
     */
    async set(name:string, value:any):Promise<void>
    {
        let tmp = Helper.serialize(value);
        await localforage.setItem(name, tmp);
    }

    /**
     * Retrieves and deserializes a value from localForage.
     * @param name - The key of the value to retrieve.
     * @returns A Promise that resolves to the deserialized value or undefined if not found.
     */
    async get(name:string):Promise<any>
    {
        let tmp = await localforage.getItem<string>(name);

        if (tmp)
        {
            return Helper.deserialize(tmp);
        }

        return undefined;
    }

    /**
     * Appends a value to an array stored in localForage.
     * If the stored value is not an array, it initializes a new array.
     * @param name - The key of the array to modify.
     * @param val - The value to append.
     * @returns A Promise that resolves when the operation is complete.
     */
    async push(name:string, val:any):Promise<void>
    {
        let tmp = await this.get(name);

        if (Helper.getType(tmp) !== "Array")
        {
            tmp = [];
        }

        tmp.push(val);
        await this.set(name, tmp);
    }

    /**
     * Removes and returns the last element of an array stored in localForage.
     * @param name - The key of the array to modify.
     * @returns A Promise that resolves to the removed element or undefined if the value is not an array or is empty.
     */
    async pop(name:string):Promise<any>
    {
        let tmp = await this.get(name);

        if (tmp && Helper.getType(tmp) === "Array")
        {
            let val = tmp.pop();
            await this.set(name, tmp);
            return val;
        }
        
        return undefined;
    }

    /**
     * Sets a value at a specific index in an array stored in localForage.
     * @param name - The key of the array to modify.
     * @param index - The index at which to set the value.
     * @param val - The value to set.
     * @returns A Promise that resolves when the operation is complete.
     */
    async setAt(name:string, index:number, val:any):Promise<void>
    {
        let tmp = await this.get(name);

        if (tmp && Helper.getType(tmp) === "Array")
        {
            tmp[index] = val;
            await this.set(name, tmp);
        }
    }

    /**
     * Removes elements from an array stored in localForage and returns them.
     * @param name - The key of the array to modify.
     * @param start - The index at which to start removing elements.
     * @param deleteAmount - The number of elements to remove.
     * @returns A Promise that resolves to the removed elements or undefined if the value is not an array.
     */
    async splice(name:string, start:number, deleteAmount:number):Promise<Array<any> | undefined>
    {
        let tmp = await this.get(name);
        if (tmp && Helper.getType(tmp) === "Array")
        {
            let ret = tmp.splice(start, deleteAmount);
            await this.set(name, tmp);
            return ret;
        }
        return undefined;
    }

    /**
     * Retrieves a value at a specific index from an array stored in localForage.
     * @param name - The key of the array.
     * @param index - The index of the value to retrieve.
     * @returns A Promise that resolves to the value at the specified index or undefined if the value is not an array.
     */
    async getAt(name:string, index:number):Promise<any>
    {
        let tmp = await this.get(name);

        if (Helper.getType(tmp) === "Array")
        {
            return tmp[index];
        }

        return undefined;
    }

    /**
     * Iterates over an array stored in localForage, executing a callback for each element.
     * Stops iteration if the callback returns false.
     * @param name - The key of the array to iterate.
     * @param callback - The function to execute for each element, receiving the element and its index.
     * @returns A Promise that resolves when the operation is complete.
     */
    async each(name:string, callback:Function):Promise<void>
    {
        let tmp = await this.get(name);

        if (tmp && Helper.getType(tmp) === "Array")
        {
            for (let i = 0; i < tmp.length; i++)
            {
                let ret = callback(tmp[i], i);

                if (!ret) 
                {
                    break;
                }
            }

            await this.set(name, tmp);
        }
    }

    /**
     * Searches an array stored in localForage for an element that satisfies the callback.
     * @param name - The key of the array to search.
     * @param callback - The function to execute for each element, returning true for a match.
     * @returns A Promise that resolves to the first matching element or undefined if no match is found or the value is not an array.
     */
    async searchFor(name:string, callback:Function):Promise<any>
    {
        let tmp = await this.get(name);

        if (tmp && Helper.getType(tmp) === "Array")
        {
            for (let i = 0; i < tmp.length; i++)
            {
                let ret = callback(tmp[i], i);

                if (ret)
                {
                    return tmp[i];
                }
            }
        }

        return undefined;
    }

    /**
     * Finds the index of the first element in an array stored in localForage that satisfies the callback.
     * @param name - The key of the array to search.
     * @param callback - The function to execute for each element, returning true for a match.
     * @returns A Promise that resolves to the index of the first matching element or -1 if no match is found or the value is not an array.
     */
    async indexOf(name:string, callback:Function):Promise<number>
    {
        let tmp = await this.get(name);

        if (Helper.getType(tmp) === "Array")
        {
            for (let i = 0; i < tmp.length; i++)
            {
                let ret = callback(tmp[i], i);

                if (ret)
                {
                    return i;
                }
            }
        }

        return -1;
    }

    /**
     * Returns the length of an array stored in localForage or the total number of keys in localForage.
     * @param name - The key of the array to check, or undefined to count all keys in localForage.
     * @returns A Promise that resolves to the length of the array or the number of keys in localForage.
     */
    async length(name:string):Promise<number>
    {
        if (name)
        {
            let tmp = await this.get(name);
            return (tmp && Helper.getType(tmp)) === "Array" ? 
                tmp.length : 0;
        }

        return await localforage.length();
    }

    /**
     * Removes a key and its value from localForage, returning the value.
     * Clears all of localForage if no key is provided.
     * @param name - The key to remove, or undefined to clear all of localForage.
     * @returns A Promise that resolves to the removed value or undefined if the key was not found.
     */
    async unset(name:string):Promise<any>
    {
        if (name)
        {
            let ret = await this.get(name);
            await localforage.removeItem(name);
            return ret;
        }

        else
        {
            // unset all
            await localforage.clear();
        }
    }

    /**
     * Removes an element at a specific index from an array stored in localForage.
     * @param name - The key of the array to modify.
     * @param index - The index of the element to remove.
     * @returns A Promise that resolves when the operation is complete.
     */
    async unsetAt(name:string, index:number):Promise<void>
    {
        let tmp = await this.get(name);

        if (Helper.getType(tmp) === "Array")
        {
            tmp.splice(index, 1);
            await this.set(name, tmp);
        }
    }

    /**
     * Alias for unset. Removes a key and its value from localForage, or clears all of localForage.
     * @param name - The key to remove, or undefined to clear all of localForage.
     * @returns A Promise that resolves to the removed value or undefined if the key was not found.
     */
    clear = this.unset;

}

const singleton = new Cache();
export default singleton;